import React, { useState, useEffect, useCallback } from "react";
import {
  ShoppingCart, Plus, Trash2, FileText, Receipt, BarChart3, Building2,
  TrendingUp, CalendarDays, Wallet, ScanLine,
} from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Input, Select, Modal, EmptyState } from "../lib/ui";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { rp, InvoiceField, InvoiceViewerModal } from "../components/purchasing";
import { JournalPreviewButton } from "../components/JournalPreview";
import OcrTab from "./OcrTab";

const FINISHED = ["INTERNAL_FINISHED_GOOD", "BRANCH_SKU"];

// ---------------- Create modal ----------------
function CreateModal({ open, onClose, onCreated, products, suppliers }) {
  const { t } = useT();
  const toast = useToast();
  const today = new Date().toISOString().slice(0, 10);
  const [supplierId, setSupplierId] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(today);
  const [notes, setNotes] = useState("");
  const [image, setImage] = useState(null);
  const [items, setItems] = useState([{ product_id: "", quantity: "", unit_price: "" }]);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setSupplierId(""); setInvoiceNo(""); setInvoiceDate(today); setNotes(""); setImage(null);
    setItems([{ product_id: "", quantity: "", unit_price: "" }]);
  };

  const setItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addRow = () => setItems((arr) => [...arr, { product_id: "", quantity: "", unit_price: "" }]);
  const delRow = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const total = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  const submit = async () => {
    const valid = items.filter((it) => it.product_id && Number(it.quantity) > 0);
    if (!valid.length) { toast.error(t("Add at least one item with quantity")); return; }
    setSaving(true);
    try {
      await api.post("/purchasing/central", {
        supplier_id: supplierId || null, invoice_no: invoiceNo || null, invoice_date: invoiceDate,
        notes: notes || null, invoice_image: image,
        items: valid.map((it) => ({ product_id: it.product_id, quantity: Number(it.quantity), unit_price: Number(it.unit_price) || 0 })),
      });
      toast.success(t("Purchase recorded — inventory & prices updated"));
      reset(); onCreated(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Something went wrong"));
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} size="xl" title={t("New Central Purchase")}
      footer={<>
        <Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button>
        <Button onClick={submit} loading={saving} data-testid="pc-central-submit"><FileText size={16} /> {t("Confirm Purchase")}</Button>
      </>}>
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Select label={t("Supplier")} value={supplierId} onChange={(e) => setSupplierId(e.target.value)} data-testid="pc-central-supplier">
            <option value="">{t("— Optional —")}</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
          <Input label={t("Invoice Number")} value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} data-testid="pc-central-invoiceno" />
          <Input label={t("Invoice Date")} type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
        </div>

        <div className="rounded-xl border border-line">
          <div className="grid grid-cols-[1fr,90px,120px,90px,40px] gap-2 border-b border-line bg-stone-100 px-3 py-2 text-xs font-semibold uppercase text-ink-mute">
            <span>{t("Product")}</span><span className="text-right">{t("Qty")}</span><span className="text-right">{t("Unit Price")}</span><span className="text-right">{t("Total")}</span><span />
          </div>
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-[1fr,90px,120px,90px,40px] items-center gap-2 px-3 py-2" data-testid={`pc-central-item-${i}`}>
              <select className="cf-input !py-1.5 !text-sm" value={it.product_id} onChange={(e) => setItem(i, "product_id", e.target.value)} data-testid={`pc-central-item-product-${i}`}>
                <option value="">{t("Select product")}</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>)}
              </select>
              <input className="cf-input !py-1.5 !text-right !text-sm" type="number" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} placeholder="0" data-testid={`pc-central-item-qty-${i}`} />
              <input className="cf-input !py-1.5 !text-right !text-sm" type="number" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} placeholder="0" data-testid={`pc-central-item-price-${i}`} />
              <span className="text-right text-sm font-semibold text-ink">{rp((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}</span>
              <button onClick={() => delRow(i)} disabled={items.length === 1} className="text-ink-mute hover:text-danger-text disabled:opacity-30"><Trash2 size={15} /></button>
            </div>
          ))}
          <div className="flex items-center justify-between border-t border-line px-3 py-2">
            <Button variant="ghost" size="sm" onClick={addRow} data-testid="pc-central-additem"><Plus size={14} /> {t("Add Item")}</Button>
            <p className="text-sm font-bold text-ink">{t("Total")}: <span className="text-primary">{rp(total)}</span></p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Input label={t("Notes")} value={notes} onChange={(e) => setNotes(e.target.value)} />
          <InvoiceField value={image} onChange={setImage} testid="pc-central-invoice" />
        </div>
        <p className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800">{t("On confirm: Central inventory increases, Product Master prices update from the invoice, and price history is recorded.")}</p>
      </div>
    </Modal>
  );
}

// ---------------- History tab ----------------
function HistoryTab({ products, suppliers }) {
  const { t } = useT();
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("create_purchase_central");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/purchasing/central"); setRows(data.rows); }
    catch { toast.error(t("Failed to load")); }
  }, [t, toast]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {canCreate && <Button onClick={() => setShowCreate(true)} data-testid="pc-central-new"><Plus size={16} /> {t("New Purchase")}</Button>}
      </div>
      <Card>
        {!rows ? <div className="flex h-32 items-center justify-center"><Spinner className="h-7 w-7" /></div>
          : rows.length === 0 ? <EmptyState title={t("No purchases yet")} subtitle={t("Record your first central purchase.")} />
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pc-central-table">
                <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-mute">
                  <tr><th className="py-2 pr-3">{t("Reference")}</th><th className="py-2 pr-3">{t("Date")}</th><th className="py-2 pr-3">{t("Supplier")}</th><th className="py-2 pr-3">{t("Invoice")}</th><th className="py-2 pr-3 text-center">{t("Items")}</th><th className="py-2 pr-3 text-right">{t("Total")}</th><th className="py-2 pr-3" /></tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={`pc-central-row-${r.reference_no}`}>
                      <td className="py-2.5 pr-3 font-mono text-xs font-semibold text-primary">{r.reference_no}</td>
                      <td className="py-2.5 pr-3 text-xs text-ink-soft">{(r.created_at || "").slice(0, 10)}</td>
                      <td className="py-2.5 pr-3 text-ink">{r.supplier_name || "—"}</td>
                      <td className="py-2.5 pr-3 text-xs text-ink-soft">{r.invoice_no || "—"}</td>
                      <td className="py-2.5 pr-3 text-center">{r.items?.length || 0}</td>
                      <td className="py-2.5 pr-3 text-right font-semibold text-ink">{rp(r.total_value)}</td>
                      <td className="py-2.5 pr-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <JournalPreviewButton module="purchases" docId={r.id} />
                          {r.has_invoice ? <Button variant="ghost" size="sm" onClick={() => setInvoiceId(r.id)} data-testid={`pc-central-invoice-${r.reference_no}`}><Receipt size={14} /> {t("View Invoice")}</Button> : <span className="text-xs text-ink-mute">{t("No invoice")}</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
      <CreateModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={load} products={products} suppliers={suppliers} />
      <InvoiceViewerModal purchaseId={invoiceId} open={!!invoiceId} onClose={() => setInvoiceId(null)} />
    </div>
  );
}

// ---------------- Dashboard + Supplier tab ----------------
function DashboardTab() {
  const { t } = useT();
  const [dash, setDash] = useState(null);
  const [suppliers, setSuppliers] = useState(null);

  useEffect(() => {
    api.get("/purchasing/dashboard/summary").then(({ data }) => setDash(data)).catch(() => {});
    api.get("/purchasing/suppliers/analytics").then(({ data }) => setSuppliers(data.rows)).catch(() => {});
  }, []);

  if (!dash) return <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div>;
  const c = dash.central || {};
  return (
    <div className="space-y-5" data-testid="pc-central-dashboard">
      <div className="grid gap-4 sm:grid-cols-3">
        <KpiCard icon={CalendarDays} tone="info" label={t("Purchases Today")} value={c.purchases_today ?? 0} />
        <KpiCard icon={ShoppingCart} tone="primary" label={t("Purchases This Month")} value={c.purchases_month ?? 0} />
        <KpiCard icon={Wallet} tone="success" label={t("Total Value (Month)")} value={rp(c.total_value_month)} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-ink"><Building2 size={18} /> {t("Top Suppliers (Month)")}</h3>
          {(c.top_suppliers || []).length === 0 ? <p className="text-sm text-ink-mute">{t("No data")}</p> : (
            <div className="space-y-2">
              {c.top_suppliers.map((s, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2">
                  <span className="text-sm font-medium text-ink">{i + 1}. {s.supplier_name}</span>
                  <span className="text-sm font-bold text-primary">{rp(s.spend)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card>
          <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-ink"><TrendingUp size={18} /> {t("Supplier Analytics")}</h3>
          {!suppliers ? <Spinner /> : suppliers.length === 0 ? <p className="text-sm text-ink-mute">{t("No supplier-linked purchases yet")}</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pc-supplier-table">
                <thead className="border-b border-line text-left text-xs uppercase text-ink-mute"><tr><th className="py-2 pr-3">{t("Supplier")}</th><th className="py-2 pr-3 text-right">{t("Spend")}</th><th className="py-2 pr-3 text-center">{t("Count")}</th><th className="py-2 pr-3">{t("Last")}</th></tr></thead>
                <tbody className="divide-y divide-line">
                  {suppliers.map((s) => (
                    <tr key={s.supplier_id}>
                      <td className="py-2 pr-3 font-medium text-ink">{s.supplier_name}</td>
                      <td className="py-2 pr-3 text-right">{rp(s.total_spend)}</td>
                      <td className="py-2 pr-3 text-center">{s.purchase_count}</td>
                      <td className="py-2 pr-3 text-xs text-ink-soft">{s.last_purchase || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

const KpiCard = ({ icon: Icon, tone, label, value }) => {
  const tones = { info: "bg-blue-100 text-blue-700", primary: "bg-primary-light text-primary", success: "bg-success-light text-success-text" };
  return (
    <Card className="flex items-center gap-3">
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${tones[tone]}`}><Icon size={20} /></div>
      <div><p className="font-heading text-xl font-bold text-ink">{value}</p><p className="text-xs text-ink-mute">{label}</p></div>
    </Card>
  );
};

// ---------------- Page ----------------
export default function PembelianCentral() {
  const { t } = useT();
  const [tab, setTab] = useState("history");
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { active_only: true } }).then(({ data }) => {
      const list = Array.isArray(data) ? data : data.rows || [];
      setProducts(list.filter((p) => !FINISHED.includes(p.product_type)));
    }).catch(() => {});
    api.get("/suppliers").then(({ data }) => setSuppliers(Array.isArray(data) ? data : data.rows || [])).catch(() => {});
  }, []);

  const tabs = [{ id: "history", label: t("Purchases"), icon: Receipt }, { id: "ocr", label: t("Invoice OCR"), icon: ScanLine }, { id: "dashboard", label: t("Dashboard & Suppliers"), icon: BarChart3 }];

  return (
    <div data-testid="pembelian-central-page">
      <PageHeader title={t("Central Purchase")} subtitle={t("Record raw-material purchases — inventory & price updates")} icon={ShoppingCart} />
      <div className="mb-5 inline-flex rounded-xl border border-line bg-white p-1">
        {tabs.map((tb) => (
          <button key={tb.id} data-testid={`pc-central-tab-${tb.id}`} onClick={() => setTab(tb.id)}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === tb.id ? "bg-primary text-white shadow-sm" : "text-ink-soft hover:bg-stone-100"}`}>
            <tb.icon size={16} /> {tb.label}
          </button>
        ))}
      </div>
      {tab === "history" ? <HistoryTab products={products} suppliers={suppliers} />
        : tab === "ocr" ? <OcrTab products={products} />
        : <DashboardTab />}
    </div>
  );
}
